import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  croppedBitmap, detectPixelSize, detectSpriteGrid, gridCounts, gridSprites, imageFromBlob, otsuThreshold, sampleBitmap, trimmedBitmap,
} from '../../utils/pixelSheet';
import { addCustomOneBitIcons } from '../../services/oneBitCustomService';
import { BitmapThumb } from './OneBitPickerDialog';

// Pasted pixel art that was scaled up: finds the real pixel size, shows it as a bitmap, and lets a
// rectangle be cropped from it to place on the grid.
function NonoPixelSheet({ file, onFile, onSelect, onCancel }) {
  const canvasRef = useRef(null);
  const fileInput = useRef(null);
  const drag = useRef(null);
  const [image, setImage] = useState(null);
  const [error, setError] = useState(null);
  const [params, setParams] = useState(null);
  const [threshold, setThreshold] = useState(128);
  const [zoom, setZoom] = useState(6);
  const [sel, setSel] = useState(null);
  const [trim, setTrim] = useState(true);
  const [splitting, setSplitting] = useState(false);
  const [grid, setGrid] = useState(null);
  const [minInk, setMinInk] = useState(4);
  const [skipped, setSkipped] = useState(() => new Set());
  const [libraryName, setLibraryName] = useState('');
  const [adding, setAdding] = useState(null);

  useEffect(() => {
    if (!file) return undefined;
    let live = true;
    setError(null);
    imageFromBlob(file).then(img => {
      if (!live) return;
      setImage(img);
      setParams(detectPixelSize(img));
      setThreshold(otsuThreshold(img.lum));
      setSel(null);
    }).catch(e => live && setError(e.message));
    return () => { live = false; };
  }, [file]);

  const bitmap = useMemo(
    () => (image && params && params.size >= 1 ? sampleBitmap(image, params.size, params.ox, params.oy, threshold) : null),
    [image, params, threshold]
  );

  const cellAt = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    return {
      x: Math.max(0, Math.min(bitmap.w - 1, Math.floor((e.clientX - rect.left) / zoom))),
      y: Math.max(0, Math.min(bitmap.h - 1, Math.floor((e.clientY - rect.top) / zoom))),
    };
  };

  const onPointerDown = (e) => {
    if (!bitmap) return;
    e.preventDefault();
    canvasRef.current.setPointerCapture(e.pointerId);
    const p = cellAt(e);
    drag.current = p;
    setSel({ x0: p.x, y0: p.y, x1: p.x, y1: p.y });
  };

  const onPointerMove = (e) => {
    if (!drag.current) return;
    const p = cellAt(e), a = drag.current;
    setSel({ x0: Math.min(a.x, p.x), y0: Math.min(a.y, p.y), x1: Math.max(a.x, p.x), y1: Math.max(a.y, p.y) });
  };

  const selected = useMemo(() => {
    if (!bitmap || !sel) return null;
    const crop = croppedBitmap(bitmap, sel.x0, sel.y0, sel.x1, sel.y1);
    return trim ? trimmedBitmap(crop) : crop;
  }, [bitmap, sel, trim]);

  // Sprite splitting works on the selection, or on the whole sheet when nothing is selected.
  const sprites = useMemo(() => {
    if (!splitting || !bitmap) return [];
    const area = sel ? croppedBitmap(bitmap, sel.x0, sel.y0, sel.x1, sel.y1) : bitmap;
    return grid ? gridSprites(area, grid, minInk) : [];
  }, [splitting, bitmap, sel, minInk, grid]);
  const counts = useMemo(() => {
    if (!splitting || !bitmap || !grid) return null;
    return gridCounts(sel ? croppedBitmap(bitmap, sel.x0, sel.y0, sel.x1, sel.y1) : bitmap, grid);
  }, [splitting, bitmap, sel, grid]);
  const chosen = sprites.filter((_, i) => !skipped.has(i));

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !bitmap) return;
    canvas.width = bitmap.w * zoom;
    canvas.height = bitmap.h * zoom;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = '#16181d';
    bitmap.bits.forEach((v, i) => v && ctx.fillRect((i % bitmap.w) * zoom, Math.floor(i / bitmap.w) * zoom, zoom, zoom));
    if (zoom >= 6) {
      ctx.strokeStyle = 'rgba(0,0,0,0.08)';
      ctx.lineWidth = 1;
      ctx.beginPath();
      for (let x = 0; x <= bitmap.w; x++) { ctx.moveTo(x * zoom + 0.5, 0); ctx.lineTo(x * zoom + 0.5, canvas.height); }
      for (let y = 0; y <= bitmap.h; y++) { ctx.moveTo(0, y * zoom + 0.5); ctx.lineTo(canvas.width, y * zoom + 0.5); }
      ctx.stroke();
    }
    if (splitting && grid && counts) {
      const gx = sel ? sel.x0 : 0, gy = sel ? sel.y0 : 0;
      ctx.strokeStyle = 'rgba(224, 49, 49, 0.85)';
      ctx.lineWidth = 1;
      ctx.beginPath();
      for (let c = 0; c <= counts.cols; c++) { const x = (gx + grid.ox + c * grid.cw) * zoom + 0.5; ctx.moveTo(x, (gy + grid.oy) * zoom); ctx.lineTo(x, (gy + grid.oy + counts.rows * grid.ch) * zoom); }
      for (let r = 0; r <= counts.rows; r++) { const y = (gy + grid.oy + r * grid.ch) * zoom + 0.5; ctx.moveTo((gx + grid.ox) * zoom, y); ctx.lineTo((gx + grid.ox + counts.cols * grid.cw) * zoom, y); }
      ctx.stroke();
    }
    if (sel) {
      ctx.fillStyle = 'rgba(47, 91, 234, 0.18)';
      ctx.fillRect(sel.x0 * zoom, sel.y0 * zoom, (sel.x1 - sel.x0 + 1) * zoom, (sel.y1 - sel.y0 + 1) * zoom);
      ctx.strokeStyle = '#2f5bea';
      ctx.lineWidth = 2;
      ctx.strokeRect(sel.x0 * zoom + 1, sel.y0 * zoom + 1, (sel.x1 - sel.x0 + 1) * zoom - 2, (sel.y1 - sel.y0 + 1) * zoom - 2);
    }
  }, [bitmap, zoom, sel, splitting, grid, counts]);

  const detectGrid = () => {
    const area = sel ? croppedBitmap(bitmap, sel.x0, sel.y0, sel.x1, sel.y1) : bitmap;
    setGrid(detectSpriteGrid(area) || { cw: 16, ch: 16, ox: 0, oy: 0 });
    setSkipped(new Set());
  };
  const startSplitting = () => {
    detectGrid();
    setSplitting(true);
    setSkipped(new Set());
    setAdding(null);
  };
  const setGridValue = (key, value) => { setGrid(g => ({ ...g, [key]: Math.max(0, Math.round(value) || 0) })); setSkipped(new Set()); };

  const addToLibrary = async () => {
    setAdding({ busy: true });
    try {
      await addCustomOneBitIcons(chosen, libraryName.trim());
      setAdding({ done: chosen.length });
    } catch (e) {
      setAdding({ error: `Lisäys epäonnistui: ${e.message}` });
    }
  };

  const use = () => selected && selected.bits.includes(1) && onSelect(selected);

  useEffect(() => {
    const onKey = (e) => {
      if (['INPUT', 'TEXTAREA', 'SELECT'].includes(document.activeElement?.tagName) && e.key !== 'Escape') return;
      if (e.key === 'Escape') { e.preventDefault(); if (splitting) setSplitting(false); else onCancel(); }
      else if (e.key === 'Enter' && !splitting) { e.preventDefault(); use(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  const setParam = (key, value) => setParams(p => ({ ...p, [key]: value }));
  const num = (v) => (Number.isFinite(v) ? v : 0);

  return (
    <div className="nn-tracer nn-sheet">
      <div className="nn-sheet-view">
        {!image && !error && (
          <div className="nn-sheet-drop" onClick={() => fileInput.current?.click()}>
            Liitä pikselikuva (Ctrl+V), pudota tiedosto tähän tai klikkaa valitaksesi tiedoston.
          </div>
        )}
        {error && <div className="tl-error">{error}</div>}
        {bitmap && <canvas ref={canvasRef} className="nn-sheet-canvas" onPointerDown={onPointerDown} onPointerMove={onPointerMove}
          onPointerUp={() => { drag.current = null; }} onPointerCancel={() => { drag.current = null; }} />}
        {splitting && (
          <div className="nn-icon-grid nn-bit-grid">
            {!sprites.length && <div className="emoji-note">Ei spritejä. Kokeile pienempää väliä tai vähemmän vähimmäisruutuja.</div>}
            {sprites.map((sp, i) => (
              <button key={i} type="button" className={`nn-icon-btn nn-bit-btn nn-sprite ${skipped.has(i) ? 'off' : ''}`} title={`${sp.w}×${sp.h}`}
                onClick={() => setSkipped(prev => { const next = new Set(prev); if (next.has(i)) next.delete(i); else next.add(i); return next; })}>
                <BitmapThumb bitmap={sp} />
                <span className="nn-icon-name">{sp.w}×{sp.h}</span>
              </button>
            ))}
          </div>
        )}
      </div>
      <div className="nn-tracer-panel">
        <input ref={fileInput} type="file" accept="image/*" hidden
          onChange={e => { const f = e.target.files?.[0]; e.target.value = ''; if (f) onFile(f); }} />
        <div className="nn-row">
          <button className="btn-secondary" onClick={() => fileInput.current?.click()}>Valitse tiedosto</button>
          <button className="btn-secondary" disabled={!image} onClick={() => setParams(detectPixelSize(image))}>Tunnista uudelleen</button>
        </div>
        {splitting ? (
          <>
            <span className="nn-hint">
              Toimii vain täydellisille ruudukoille: punaiset viivat näyttävät ruudut ({counts ? `${counts.cols}×${counts.rows}` : '?'}), ja jokaisesta
              ruudusta tulee yksi sprite. Säädä kokoa ja siirtymää kunnes viivat osuvat välien kohdalle. Rajaa kuvasta ensin ruudukon alue
              raahaamalla, jos reunoilla on ylimääräistä. Klikkaa sprite pois tai takaisin; harmaat jäävät pois.
            </span>
            <button className="btn-secondary" onClick={detectGrid}>Tunnista ruudukko uudelleen</button>
            {grid && (
              <>
                <div className="nn-row">
                  Ruutu
                  <input type="number" className="nn-size" min={2} value={grid.cw} title="Spriten ruudun leveys" onChange={e => setGridValue('cw', Number(e.target.value))} />
                  ×
                  <input type="number" className="nn-size" min={2} value={grid.ch} title="Spriten ruudun korkeus" onChange={e => setGridValue('ch', Number(e.target.value))} />
                </div>
                <div className="nn-row">
                  Siirtymä
                  <input type="number" className="nn-size" min={0} value={grid.ox} onChange={e => setGridValue('ox', Number(e.target.value))} />
                  <input type="number" className="nn-size" min={0} value={grid.oy} onChange={e => setGridValue('oy', Number(e.target.value))} />
                </div>
              </>
            )}
            <div className="nn-row">
              Min. ruutuja
              <input type="number" className="nn-size" min={1} value={minInk} title="Tätä pienemmät roskat jätetään pois"
                onChange={e => { setMinInk(Math.max(1, Number(e.target.value) || 1)); setSkipped(new Set()); }} />
            </div>
            <label className="nn-title">
              Nimi hakua varten (valinnainen)
              <input value={libraryName} placeholder="esim. emoji" onChange={e => setLibraryName(e.target.value)} />
            </label>
            <div className="nn-row">
              <button className="btn-secondary" onClick={() => setSkipped(new Set())}>Kaikki</button>
              <button className="btn-secondary" onClick={() => setSkipped(new Set(sprites.map((_, i) => i)))}>Ei yhtään</button>
            </div>
            {adding?.error && <div className="tl-error">{adding.error}</div>}
            {adding?.done != null && <div className="nn-status nn-status-logic">Lisätty {adding.done} kuvaa 1-bit-valitsimeen.</div>}
            <div className="nn-row">
              <button className="btn-secondary" onClick={() => { setSplitting(false); setAdding(null); }}>Takaisin <kbd>Esc</kbd></button>
              <button className="btn-primary" disabled={!chosen.length || adding?.busy} onClick={addToLibrary}>Lisää {chosen.length} 1-bit-valitsimeen</button>
            </div>
          </>
        ) : image && params && (
          <>
            <span className="nn-hint">
              Kuva {image.w}×{image.h} px, pikselikoko {params.size} px, eli {bitmap ? `${bitmap.w}×${bitmap.h}` : '?'} pikseliä.
              Jos kuva näyttää rikkinäiseltä, säädä kokoa ja siirtymiä.
            </span>
            <div className="nn-row">
              Pikselikoko
              <input type="number" className="nn-size" min={1} max={80} step={0.01} value={params.size}
                onChange={e => setParam('size', num(Number(e.target.value)))} />
              <button className="btn-secondary" onClick={() => setParams({ size: 1, ox: 0, oy: 0 })} title="Kuvaa ei ole suurennettu">1:1</button>
            </div>
            <div className="nn-row">
              Siirtymä
              <input type="number" className="nn-size" min={0} step={0.1} value={params.ox} title="Vaakasiirtymä (px)"
                onChange={e => setParam('ox', num(Number(e.target.value)))} />
              <input type="number" className="nn-size" min={0} step={0.1} value={params.oy} title="Pystysiirtymä (px)"
                onChange={e => setParam('oy', num(Number(e.target.value)))} />
            </div>
            <label className="nn-slider">
              <span>Kynnys <b>{threshold}</b></span>
              <input type="range" min={1} max={254} value={threshold} onChange={e => setThreshold(Number(e.target.value))} />
            </label>
            <label className="nn-slider">
              <span>Zoom <b>{zoom}×</b></span>
              <input type="range" min={2} max={16} value={zoom} onChange={e => setZoom(Number(e.target.value))} />
            </label>
            <label className="nn-check"><input type="checkbox" checked={trim} onChange={e => setTrim(e.target.checked)} /> Rajaa tyhjät reunat</label>
            <button className="btn-secondary" disabled={!bitmap} onClick={startSplitting}
              title="Etsii kuvasta erilliset spritet ja lisää ne 1-bit-valitsimeen">Etsi spritet</button>
            <div className="nn-status nn-status-empty">
              {selected ? `Valinta ${selected.w}×${selected.h}` : 'Valitse alue raahaamalla kuvasta'}
            </div>
          </>
        )}
        {!splitting && <div className="nn-row">
          <button className="btn-secondary" onClick={onCancel}>Peruuta <kbd>Esc</kbd></button>
          <button className="btn-primary" disabled={!selected || !selected.bits.includes(1)} onClick={use}>Käytä valintaa <kbd>Enter</kbd></button>
        </div>}
        {!splitting && <p className="nn-hint">Valittu alue avautuu tavalliseen pikselityökaluun, jossa sitä voi vielä pienentää, kiertää ja poistaa rivejä.</p>}
      </div>
    </div>
  );
}

export default NonoPixelSheet;
